import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  StyleSheet,
  View,
  ScrollView,
  TextInput,
  TouchableOpacity,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";

import { ThemedText } from "@/components/ThemedText";
import { ThemedPanel } from "@/components/ThemedPanel";
import { ThemedButton } from "@/components/ThemedButton";
import { ThemedToggle } from "@/components/ThemedToggle";
import ThemedScreen from "@/components/ThemedScreen";
import { CustomAlert } from "@/components/ThemedAlert";
import { ThemedProgressBar } from "@/components/ThemedProgressBar";
import { useAuth } from "@components/auth/useAuth";
import { useFirebase } from "@components/firebase/useFirebase";
import { getRules } from "common247";
import { useThemeColor } from "@/hooks/useThemeColor";
import { Spacing } from "@/constants/ThemeColours";
import {
  RemoteConfigService,
  type IFeatureFlagState,
} from "@/services/remote-config-service";
import {
  PLATFORM_FEATURE_FLAGS,
  type FeatureFlagCategory,
} from "@/constants/feature-flags";

/**
 * Admin Feature Toggles Screen.
 * Allows administrators to inspect, toggle, and synchronize platform feature flags
 * powered by Firebase Remote Config.
 *
 * @returns {React.JSX.Element} The rendered admin screen.
 */
export default function FeatureTogglesAdminScreen(): React.JSX.Element {
  const { user } = useAuth(true);
  const fb = useFirebase();

  const [flags, setFlags] = useState<Record<string, IFeatureFlagState>>(() =>
    RemoteConfigService.getInstance().getAllFlags()
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [updatingKey, setUpdatingKey] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");

  const textMuted = useThemeColor({}, "textMuted");
  const borderColor = useThemeColor({}, "border");
  const cardBg = useThemeColor({}, "card");
  const inputBg = useThemeColor({}, "background");
  const textColor = useThemeColor({}, "text");

  const rules = getRules();
  const isAdmin = user ? rules.isAdmin.can(user.claims, undefined) : false;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // 1. Fetch live template from backend if available
      if (fb) {
        try {
          const res = await RemoteConfigService.fetchRemoteConfigTemplate(fb);
          if (res.status && res.parameters) {
            const currentFlags = RemoteConfigService.getInstance().getAllFlags();
            for (const [k, p] of Object.entries(res.parameters)) {
              if (currentFlags[k]) {
                currentFlags[k].value = Boolean(p.value);
                currentFlags[k].source = p.source;
              }
            }
            setFlags({ ...currentFlags });
          }
        } catch (backendErr) {
          console.info("[FeatureToggles] Backend fetch deferred, reading local state:", backendErr);
        }
      }

      // 2. Refresh local Remote Config client
      await RemoteConfigService.getInstance().fetchAndActivate();
      setFlags(RemoteConfigService.getInstance().getAllFlags());
    } catch (err: any) {
      console.error("[FeatureToggles] Failed to refresh flags:", err);
      CustomAlert.alert("Error", err?.message || "Failed to load feature flags.");
    } finally {
      setLoading(false);
    }
  }, [fb]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (!user || !isAdmin) {
    return (
      <ThemedScreen title="Feature Toggles">
        <View style={styles.unauthorizedContainer}>
          <ThemedText type="subtitle" style={{ color: "#E74C3C" }}>
            Access Denied
          </ThemedText>
          <ThemedText style={{ color: textMuted, marginTop: 8 }}>
            This configuration console is restricted strictly to platform administrators.
          </ThemedText>
        </View>
      </ThemedScreen>
    );
  }

  const handleToggle = async (key: string, nextValue: boolean) => {
    setUpdatingKey(key);
    try {
      const def = PLATFORM_FEATURE_FLAGS[key];
      const desc = def?.description || "";

      // 1. Set local override immediately for instant UI feedback
      await RemoteConfigService.getInstance().setLocalOverride(key, nextValue);
      setFlags(RemoteConfigService.getInstance().getAllFlags());

      // 2. Sync to Cloud Remote Config / backend if reachable
      if (fb) {
        try {
          await RemoteConfigService.updateRemoteConfigParameter(fb, key, nextValue, desc);
        } catch (cloudErr) {
          console.warn("[FeatureToggles] Cloud Remote Config update skipped (local override active):", cloudErr);
        }
      }

      CustomAlert.alert(
        "Feature Toggle Updated",
        `Feature '${def?.name || key}' is now ${nextValue ? "ENABLED" : "DISABLED"}.`
      );
    } catch (err: any) {
      console.error("[FeatureToggles] Error updating toggle:", err);
      CustomAlert.alert("Error", err?.message || "Failed to update feature toggle.");
    } finally {
      setUpdatingKey(null);
    }
  };

  const handleClearOverrides = async () => {
    CustomAlert.alert(
      "Reset Local Overrides",
      "Are you sure you want to revert all local developer overrides to Remote Config defaults?",
      [
        { text: "Cancel", style: "cancel" },
        {
          text: "Reset All",
          style: "destructive",
          onPress: async () => {
            setLoading(true);
            try {
              await RemoteConfigService.getInstance().clearAllOverrides();
              await loadData();
              CustomAlert.alert("Success", "All local feature overrides have been cleared.");
            } catch (err: any) {
              CustomAlert.alert("Error", err?.message || "Failed to clear overrides.");
            } finally {
              setLoading(false);
            }
          },
        },
      ]
    );
  };

  // Filtered flag list
  const filteredFlags = useMemo(() => {
    return Object.values(flags).filter((flag) => {
      const matchesCategory =
        selectedCategory === "all" || flag.category === selectedCategory;
      const q = searchQuery.toLowerCase().trim();
      const matchesQuery =
        !q ||
        flag.name.toLowerCase().includes(q) ||
        flag.key.toLowerCase().includes(q) ||
        flag.description.toLowerCase().includes(q);
      return matchesCategory && matchesQuery;
    });
  }, [flags, selectedCategory, searchQuery]);

  const categories: { key: string; label: string }[] = [
    { key: "all", label: "All Flags" },
    { key: "editor", label: "Editor" },
    { key: "media", label: "Media" },
    { key: "messaging", label: "Messaging" },
    { key: "safety", label: "Safety" },
    { key: "diagnostics", label: "Diagnostics" },
    { key: "navigation", label: "Navigation" },
  ];

  return (
    <ThemedScreen title="Feature Toggles">
      <ScrollView contentContainerStyle={styles.scrollContainer}>
        <ThemedProgressBar loading={loading || updatingKey !== null} />

        {/* Section 1: Overview & Controls */}
        <ThemedPanel
          title="Remote Config Console"
          subtitle="Manage runtime feature flags and gradual rollout toggles"
          variant="accent"
          accentColor="#2980B9"
          style={styles.panelSpacing}
        >
          <ThemedText style={{ color: textMuted, marginBottom: 12 }}>
            Feature flags empower administrators to enable or disable platform capabilities dynamically
            across Web, iOS, and Android without deploying new builds.
          </ThemedText>

          <View style={styles.actionRow}>
            <ThemedButton
              title="Refresh from Cloud"
              onPress={loadData}
              disabled={loading}
              variant="secondary"
              icon={<Ionicons name="cloud-download-outline" size={16} color="#2980B9" />}
            />
            <ThemedButton
              title="Clear Local Overrides"
              onPress={handleClearOverrides}
              disabled={loading}
              variant="outline"
              icon={<Ionicons name="refresh-outline" size={16} color={textMuted} />}
            />
          </View>
        </ThemedPanel>

        {/* Section 2: Search & Filter Toolbar */}
        <View style={[styles.toolbarContainer, { backgroundColor: cardBg, borderColor }]}>
          <View style={[styles.searchBox, { backgroundColor: inputBg, borderColor }]}>
            <Ionicons name="search" size={18} color={textMuted} style={styles.searchIcon} />
            <TextInput
              style={[styles.searchInput, { color: textColor }]}
              placeholder="Search feature flags by name or key..."
              placeholderTextColor={textMuted}
              value={searchQuery}
              onChangeText={setSearchQuery}
              autoCapitalize="none"
              autoCorrect={false}
            />
            {searchQuery.length > 0 && (
              <TouchableOpacity onPress={() => setSearchQuery("")} style={styles.clearSearchBtn}>
                <Ionicons name="close-circle" size={18} color={textMuted} />
              </TouchableOpacity>
            )}
          </View>

          {/* Category Chips */}
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.chipsScroll}>
            {categories.map((cat) => {
              const isSelected = selectedCategory === cat.key;
              return (
                <TouchableOpacity
                  key={cat.key}
                  style={[
                    styles.chip,
                    {
                      backgroundColor: isSelected ? "#2980B9" : inputBg,
                      borderColor: isSelected ? "#2980B9" : borderColor,
                    },
                  ]}
                  onPress={() => setSelectedCategory(cat.key)}
                >
                  <ThemedText
                    type="defaultSemiBold"
                    style={{
                      fontSize: 12,
                      color: isSelected ? "#FFFFFF" : textMuted,
                    }}
                  >
                    {cat.label}
                  </ThemedText>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        </View>

        {/* Section 3: Feature Toggles List */}
        <ThemedPanel
          title={`Active Feature Flags (${filteredFlags.length})`}
          subtitle="Real-time toggles and fallback status"
          style={styles.panelSpacing}
        >
          {filteredFlags.length === 0 ? (
            <View style={styles.emptyContainer}>
              <Ionicons name="filter-outline" size={32} color={textMuted} />
              <ThemedText style={{ color: textMuted, marginTop: 8 }}>
                No feature flags matched your current search and filter criteria.
              </ThemedText>
            </View>
          ) : (
            <View style={styles.flagList}>
              {filteredFlags.map((flag) => {
                const isUpdating = updatingKey === flag.key;
                return (
                  <View
                    key={flag.key}
                    style={[
                      styles.flagCard,
                      { backgroundColor: cardBg, borderColor },
                    ]}
                  >
                    <View style={styles.flagHeaderRow}>
                      <View style={styles.flagTitleBlock}>
                        <ThemedText type="defaultSemiBold" style={styles.flagName}>
                          {flag.name}
                        </ThemedText>
                        <ThemedText style={[styles.flagKey, { color: textMuted }]}>
                          {flag.key}
                        </ThemedText>
                      </View>

                      {/* Source & Category Badges */}
                      <View style={styles.badgeRow}>
                        <View
                          style={[
                            styles.badge,
                            {
                              backgroundColor:
                                flag.source === "override"
                                  ? "#F39C1220"
                                  : flag.source === "remote"
                                  ? "#2980B920"
                                  : "#27AE6020",
                            },
                          ]}
                        >
                          <ThemedText
                            style={[
                              styles.badgeText,
                              {
                                color:
                                  flag.source === "override"
                                    ? "#D35400"
                                    : flag.source === "remote"
                                    ? "#2980B9"
                                    : "#27AE60",
                              },
                            ]}
                          >
                            {flag.source.toUpperCase()}
                          </ThemedText>
                        </View>
                        <View style={[styles.badge, { backgroundColor: "#8E44AD20" }]}>
                          <ThemedText style={[styles.badgeText, { color: "#8E44AD" }]}>
                            {flag.category.toUpperCase()}
                          </ThemedText>
                        </View>
                      </View>
                    </View>

                    <ThemedText style={[styles.flagDesc, { color: textMuted }]}>
                      {flag.description}
                    </ThemedText>

                    {/* Toggle Control */}
                    <View style={styles.toggleRow}>
                      <ThemedToggle
                        value={flag.value}
                        onValueChange={(newVal) => handleToggle(flag.key, newVal)}
                        disabled={loading || isUpdating}
                        label={flag.value ? "Status: ENABLED" : "Status: DISABLED"}
                        labelPosition="left"
                      />
                    </View>
                  </View>
                );
              })}
            </View>
          )}
        </ThemedPanel>
      </ScrollView>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  scrollContainer: {
    padding: Spacing.md,
    paddingBottom: 64,
  },
  unauthorizedContainer: {
    padding: Spacing.xl,
    alignItems: "center",
    justifyContent: "center",
  },
  panelSpacing: {
    marginBottom: Spacing.md,
  },
  actionRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: Spacing.sm,
    marginTop: Spacing.xs,
  },
  toolbarContainer: {
    borderWidth: 1,
    padding: Spacing.md,
    marginBottom: Spacing.md,
  },
  searchBox: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    paddingHorizontal: Spacing.sm,
    height: 44,
  },
  searchIcon: {
    marginRight: Spacing.xs,
  },
  searchInput: {
    flex: 1,
    height: "100%",
    fontSize: 14,
  },
  clearSearchBtn: {
    padding: 4,
  },
  chipsScroll: {
    marginTop: Spacing.sm,
  },
  chip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    marginRight: 8,
    borderWidth: 1,
  },
  flagList: {
    gap: Spacing.md,
  },
  flagCard: {
    borderWidth: 1,
    padding: Spacing.md,
  },
  flagHeaderRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: Spacing.xs,
  },
  flagTitleBlock: {
    flex: 1,
    minWidth: 200,
  },
  flagName: {
    fontSize: 16,
  },
  flagKey: {
    fontSize: 12,
    fontFamily: "monospace",
    marginTop: 2,
  },
  badgeRow: {
    flexDirection: "row",
    gap: 6,
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  badgeText: {
    fontSize: 10,
    fontWeight: "700",
  },
  flagDesc: {
    fontSize: 13,
    lineHeight: 18,
    marginBottom: Spacing.sm,
  },
  toggleRow: {
    borderTopWidth: 1,
    borderTopColor: "rgba(0,0,0,0.06)",
    paddingTop: Spacing.sm,
  },
  emptyContainer: {
    padding: Spacing.xl,
    alignItems: "center",
    justifyContent: "center",
  },
});
